import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth, isStaff } from "@/context/AuthContext";
import { useFetch } from "@/lib/api";

const NAV = [
  ["/eventi", "Eventi"], ["/classifiche", "Classifiche"], ["/risultati", "Risultati"], ["/archivio", "Archivio"],
  ["/clan", "Clan"], ["/news", "News"], ["/alleanza", "Alleanza"],
];

export function Logo({ size = "md" }) {
  const big = size === "lg";
  return (
    <span className="inline-flex items-center gap-2.5" data-testid="brand-logo">
      <img src="/images/logo-mark.png" alt="" width="40" height="40" className={`${big ? "w-12 h-12" : "w-9 h-9"} object-contain`} />
      <span className={`font-display font-bold uppercase leading-none whitespace-nowrap ${big ? "text-2xl" : "text-base"} tracking-[0.18em]`}>
        <span className="text-[#F5F5F7]">Nexum</span> <span className="text-[#962FFF]">Legacy</span>
      </span>
    </span>
  );
}

function UserButton({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    return <Link data-testid="header-login-button" to="/login" onClick={onNavigate} className="btn-primary btn-sm"><User className="w-4 h-4" /> Accedi</Link>;
  }
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      {isStaff(user) && (
        <Link data-testid="header-dashboard-link" to="/dashboard" onClick={onNavigate} className="btn-ghost btn-sm"><LayoutDashboard className="w-4 h-4" /> Dashboard</Link>
      )}
      <Link data-testid="header-area-link" to="/area" onClick={onNavigate} className="btn-primary btn-sm"><User className="w-4 h-4" /> Area personale</Link>
      <button data-testid="header-logout-button" aria-label="Esci" onClick={async () => { await logout(); onNavigate?.(); navigate("/"); }} className="btn-ghost btn-sm px-2.5"><LogOut className="w-4 h-4" /></button>
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) => `text-[13px] uppercase tracking-[0.14em] font-display font-semibold transition-colors duration-200 ${isActive ? "text-[#F5F5F7]" : "text-[#C6C6CE]/70 hover:text-[#F5F5F7]"}`;
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#050505]/75 backdrop-blur-xl border-b border-[#C6C6CE]/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-home-link" aria-label="Home NEXUM LEGACY"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Navigazione principale">
          {NAV.map(([to, label]) => <NavLink key={to} to={to} className={linkCls} data-testid={`nav-${label.toLowerCase()}`}>{label}</NavLink>)}
        </nav>
        <div className="hidden lg:block"><UserButton /></div>
        <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 text-[#F5F5F7]" aria-label="Apri menu" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#C6C6CE]/10 bg-[#050505] px-5 py-6 space-y-5 fade-up">
          <nav className="flex flex-col gap-4">
            {NAV.map(([to, label]) => <NavLink key={to} to={to} onClick={() => setOpen(false)} className={linkCls} data-testid={`mobile-nav-${label.toLowerCase()}`}>{label}</NavLink>)}
            <NavLink to="/staff" onClick={() => setOpen(false)} className={linkCls}>Staff</NavLink>
            <NavLink to="/regolamenti" onClick={() => setOpen(false)} className={linkCls}>Regolamenti</NavLink>
          </nav>
          <UserButton onNavigate={() => setOpen(false)} />
        </div>
      )}
    </header>
  );
}

const SOCIAL_LABEL = { discord: "Discord", instagram: "Instagram", youtube: "YouTube", twitch: "Twitch", x: "X", tiktok: "TikTok" };

export function Footer() {
  const { data } = useFetch("/site");
  const s = data?.settings || {};
  const socials = Object.entries(s.socials || {}).filter(([, v]) => v);
  return (
    <footer data-testid="site-footer" className="border-t border-[#C6C6CE]/10 bg-[#0B0B0F] mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo />
          <p className="mt-4 text-sm text-[#C6C6CE]/60 max-w-sm">{s.tagline || "Alleanza competitiva dedicata a Call of Duty."}</p>
          {s.email && <a data-testid="footer-email" href={`mailto:${s.email}`} className="mt-4 inline-block text-sm text-[#F5F5F7] hover:text-[#962FFF] transition-colors duration-200">{s.email}</a>}
        </div>
        <div>
          <div className="eyebrow mb-4">Istituzionale</div>
          <ul className="space-y-2 text-sm text-[#C6C6CE]/70">
            {[["/alleanza", "L'alleanza"], ["/staff", "Staff e organizzazione"], ["/regolamenti", "Regolamenti"], ["/archivio", "Archivio storico"]].map(([to, l]) => (
              <li key={to}><Link to={to} className="hover:text-[#F5F5F7] transition-colors duration-200">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4">Community</div>
          {socials.length ? (
            <ul className="space-y-2 text-sm text-[#C6C6CE]/70">
              {socials.map(([k, v]) => <li key={k}><a data-testid={`footer-social-${k}`} href={v} target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F7] transition-colors duration-200">{SOCIAL_LABEL[k]}</a></li>)}
            </ul>
          ) : <p className="text-sm text-[#C6C6CE]/40">Collegamenti social non ancora configurati.</p>}
        </div>
      </div>
      <div className="border-t border-[#C6C6CE]/10 py-6 text-center text-xs text-[#C6C6CE]/40 tracking-wider uppercase">© {new Date().getFullYear()} NEXUM LEGACY</div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#050505]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
